import { Component, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { Fabric } from '@app/models/order/work-order';
import { FabricProxyService } from '@app/proxies/item/fabric.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { RollLabelProxyService } from '@app/proxies/production/roll-label.proxy.service';

@Component({
  selector: 'app-roll-split',
  templateUrl: './roll-split.component.html',
  styleUrl: './roll-split.component.scss',
})
export class RollSplitComponent {
  private readonly rollProxyService = inject(RollProxyService);
  private readonly rollPrintProxyService = inject(RollLabelProxyService);
  private readonly fabricProxyService = inject(FabricProxyService);
  private readonly translateService = inject(TranslateService);

  searchInput = new FormControl('');
  newRollWeight = new FormControl<number | null>(null);

  parentRoll = signal<Roll | null>(null);
  fabric = signal<Fabric | null>(null);
  childrenRolls = signal<Roll[]>([]);
  isLoading = signal(0);
  expectingPrint = false;

  private readonly stationAssetId = 'WST-1';

  private translate(key: string): string {
    return this.translateService.instant(key);
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  searchRollByCode(): void {
    const code = this.searchInput.value;
    if (_.isEmpty(code)) {
      GlobalMessageUtil.warn(this.translate('CommonMessages.Warning'), this.translate('RollSplit_Messages.enterRollCode'));
      return;
    }

    this.parentRoll.set(null);
    this.childrenRolls.set([]);
    this.fabric.set(null);

    this.incrementLoading();
    this.rollProxyService
      .getRollsByNumbers([code!])
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (!_.isEmpty(response.data) && response.data) {
            const parent = response.data.find((roll) => roll.number?.toLowerCase() === code?.toLowerCase());
            if (parent) {
              this.parentRoll.set(parent);
              this.getFabric(parent.expectedFabricCode!);
              this.loadChildrenRolls(parent.number!);
            } else {
              GlobalMessageUtil.warn(this.translate('CommonMessages.Warning'), this.translate('RollSplit_Messages.parentRollNotFound'));
            }
          } else {
            GlobalMessageUtil.warn(this.translate('CommonMessages.Warning'), this.translate('RollSplit_Messages.noRollsFound'));
          }
        },
      });
  }

  private loadChildrenRolls(parentNumber: string): void {
    this.incrementLoading();
    this.rollProxyService
      // The API matches rollNumber as literal "contains" text (escaped since TASK #6244), so a regex anchor would
      // never match; the trailing hyphen keeps it to this parent's split children.
      .searchByRollNumber(`${parentNumber}-`)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          this.childrenRolls.set(response.data ?? []);
        },
      });
  }

  getFabric(fabricCode: string): void {
    this.incrementLoading();
    this.fabricProxyService
      .batchFabrics([fabricCode])
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.fabric.set(response.data[0]);
          } else {
            GlobalMessageUtil.warn(this.translate('CommonMessages.Warning'), this.translate('RollSplit_Messages.fabricNotFound'));
          }
        },
      });
  }

  getLatestQuantityForRoll(roll?: Roll | null): number {
    if (_.isNil(roll)) return 0;
    if (roll.finalWeight?.weightInLbs) {
      return roll.finalWeight.weightInLbs;
    }
    return roll.stationTimeStamps?.sort((a, b) => new Date(b.timeStamp!).getTime() - new Date(a.timeStamp!).getTime())[0]?.weightInLbs || 0;
  }

  performRollSplit(): void {
    const parent = this.parentRoll();
    const weight = this.newRollWeight.value;

    if (!parent) {
      GlobalMessageUtil.warn(this.translate('CommonMessages.Warning'), this.translate('RollSplit_Messages.searchParentFirst'));
      return;
    }

    if (!weight || weight <= 0) {
      GlobalMessageUtil.warn(this.translate('CommonMessages.Warning'), this.translate('RollSplit_Messages.enterValidWeight'));
      return;
    }

    this.incrementLoading();
    this.rollProxyService
      .rollSplit(this.stationAssetId, parent.number!, weight)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            GlobalMessageUtil.success(this.translate('CommonMessages.Success'), this.translate('RollSplit_Messages.splitSuccess'));
            this.newRollWeight.reset();
            const updatedParent = response.data.find((r) => r.number === parent.number);
            if (updatedParent) this.parentRoll.set(updatedParent);
            this.loadChildrenRolls(parent.number!);
          } else {
            GlobalMessageUtil.error(this.translate('CommonMessages.Error'), this.translate('RollSplit_Messages.splitNoData'));
          }
        },
      });
  }

  printQrCode(rollNumber: string): void {
    this.incrementLoading();
    this.rollPrintProxyService
      .getThermalLabelByRollNumber(rollNumber)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          const iframe = document.createElement('iframe');
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.top = '-9999px';
          iframe.style.width = '0px';
          iframe.style.height = '0px';
          iframe.style.visibility = 'hidden';

          document.body.appendChild(iframe);

          iframe.onload = () => {
            try {
              setTimeout(() => {
                if (iframe.contentWindow) {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }
              }, 500);
            } catch (e) {
              console.error('Error printing PDF:', e);
              window.open(url, '_blank');
              setTimeout(() => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(url);
              }, 1000);
            }
          };

          this.expectingPrint = true;
          iframe.src = url;

          setTimeout(() => {
            if (this.expectingPrint) {
              this.expectingPrint = false;
            }
          }, 1000);
        },
        error: () => {
          this.expectingPrint = false;
        },
      });
  }

  get canSplit(): boolean {
    return !!(this.parentRoll() && this.newRollWeight.value && this.newRollWeight.value > 0);
  }
}
